import {observer} from 'mobx-react'
import CONTENT from '../stores/Content'
import UI from '../stores/UI'
import HELP from '../stores/Help'
import Markdown from './Markdown'
import Slide from './Slide'
import BackgroundPicture from './BackgroundPicture'
import Cursor from './Cursor'
import Cross from '../svg/Cross'
import Spinner from '../svg/Spinner'
import Vimeo from './Vimeo'
import Video from './Video'

const SingleImage = ({src, width, height, className}) => (
  <div className={className}>
    <div className="fs slide-pad">
      <BackgroundPicture className="fs contain" src={src} width={width} height={height} />
    </div>
  </div>
)

const Caption = ({text}) => (
  <div className="slide-caption no-margins">
    <Markdown className="max middle" convert={text} />
  </div>
)

const BlockCaption = ({text, center}) => (
  <div className={`fs padx2 guttersx4`}>
    <div className={`fs ${center ? 'center txt-c' : 'bottom inr'}`}>
      <Markdown className="max--skinny" convert={text} />
    </div>
  </div>
)

class Content extends React.Component {

  /*
    Containing all the slide contents intro
    one component, so we can use it to preload
    the next slide at the same time.
  */

  constructor(props) {
    super(props)
  }

  render() {

    const fields = this.props
    const coverImage = fields.coverImage ? HELP.getMedia(fields.coverImage) : false
    const coverBleed = fields.coverImageLayout
    const firstImage = fields.firstImage
    const secondImage = fields.secondImage
    const leftBleed = fields.leftColumnLayout
    const rightBleed = fields.rightColumnLayout

    return fields.vimeo && !this.props.preloader ? (
      <Vimeo url={fields.vimeo} enterVideo={this.props.showVideoCursor} leaveVideo={this.props.hideVideoCursor} />
    ) : fields.video && !this.props.preloader ? (
      <Video file={fields.video} poster={fields.videoThumbnail} enterVideo={this.props.showVideoCursor} leaveVideo={this.props.hideVideoCursor} />
    ) : coverImage ? (
      <div className="fs">
        {coverBleed ? (
          <BackgroundPicture className={`fs ${coverImage.width > coverImage.height  ? 'cover' : 'contain'}`} {...coverImage} />
        ) : (
          <SingleImage className="fs" {...coverImage} />
        )}
        {fields.caption ? <Caption text={fields.caption} /> : null}
      </div>
    ) : firstImage || secondImage ? (
      <div className="fs">
        <div className="slide-columns">
          <div className="slide-col">
            {firstImage ? leftBleed ? (
              <BackgroundPicture className="fs cover to-top" {...HELP.getMedia(firstImage)} />
            ) : (
              <SingleImage className={`${rightBleed ? 'pad' : 'inl'} fs`} {...HELP.getMedia(firstImage)} />
            ) : fields.caption ? (
              <BlockCaption text={fields.caption} center={fields.captionLayout} />
            ) : null}
          </div>
          <div className="slide-col">
            {secondImage ? rightBleed ? (
              <BackgroundPicture className="fs cover to-top" {...HELP.getMedia(secondImage)} />
            ) : (
              <SingleImage className={`${leftBleed ? 'pad' : 'inr'} fs`} {...HELP.getMedia(secondImage)} />
            ) : fields.caption ? (
              <BlockCaption text={fields.caption} center={fields.captionLayout} />
            ) : null}
          </div>
        </div>
        {!rightBleed && !leftBleed && fields.caption ? <Caption text={fields.caption} /> : null}
      </div>
    ) : fields.caption && !this.props.preloader ? (
      <div className="padx4 fs center txt-c">
        <div className="max--skinny padx4">
          <Markdown convert={fields.caption} />
        </div>
      </div>
    ) : (
      <div className="padx4 fs center txt-c">
        {/* Nothin' */}
      </div>
    )
  }
}

class DesktopGallery extends React.Component {

  constructor(props) {
    super(props)

    this.state = {
      activeSlide: 0,
      ready: false,
      showArrow: true
    }
  }

  componentDidMount() {
    this.setState({
      ready: true
    })
  }

  back() {

    // Adding a new listener, so when view has transitioned out, we apply the route
    this.refs.el.addEventListener('transitionend', e => {
      this.props.history.goBack()
    })

    // Kicks off the style change and transition
    this.setState({
      ready: false
    })

  }
  handleClick = e => {
    e.stopPropagation()

    const length = CONTENT.currentProject.slides.length - 1;

    if (e.clientX >= (window.innerWidth * 0.5)) {
      this.setState((prevState) => {
        return {
          activeSlide: prevState.activeSlide < length ? prevState.activeSlide + 1 : 0
        }
      })
    } else {
      this.setState((prevState) => {
        return {
          activeSlide: prevState.activeSlide > 0 ? prevState.activeSlide - 1 : length
        }
      })
    }
  }

  handleClose = e => {
    e.stopPropagation()
    this.back()
  }

  toggleArrow = () => {
    this.setState((prevState) => {
      return {
        showArrow: !prevState.showArrow
      }
    })
  }

  toggleVideoControls = () => {
    this.setState((prevState) => {
      return {
        showArrow: UI.isLoading ? true : !prevState.showArrow
      }
    })
  }

  showVideoCursor = () => {
    this.setState({
      showArrow: UI.isLoading
    })
  }

  hideVideoCursor = () => {
    this.setState({
      showArrow: true
    })
  }

  render() {

    const project = CONTENT.currentProject;
    const foregroundColor = project && project.foregroundColor ? project.foregroundColor : '#000000'
    const backgroundColor = project && project.backgroundColor ? project.backgroundColor : '#FFFFFF'

    return (
      <div className={`slideshow fix fs`} onMouseOver={this.handleMouse} onClick={!UI.isTouch ? this.handleClick : e => false} onTouchEnd={this.handleClick} ref="el" style={{
        opacity: this.state.ready ? 1 : 0,
        transition: `all ${1}ms`,
      }}>
        {project && project.slides && this.state.ready ? (
          <div className="fs" style={{
            backgroundColor: backgroundColor,
            color: foregroundColor,
            opacity: 1,
            transition: `opacity ${300}ms`
          }}>

            {/* Image slides */}
            {project.slides.map(({fields}, i) => {

              const key = `${project.slug}-slide-${i}`
              const isActive = this.state.activeSlide === i

              return (
                <div className="fs" key={key}>
                  {isActive && project.slides.length > i+1 ? (
                    <div className="preloader fs" style={{
                      position: 'absolute',
                      left: '-999em',
                      visibility: 'hidden'
                    }}>
                      <Content {...project.slides[i+1].fields} preloader />
                    </div>
                  ) : null}
                  <Slide in={isActive} bg={backgroundColor} fg={foregroundColor}>
                    {true ? <Content {...fields} showVideoCursor={this.showVideoCursor} hideVideoCursor={this.hideVideoCursor} /> : null}
                  </Slide>
                </div>
              )
            })}

            {/* Close/Back button */}
            <div className="close" onClick={this.handleClose} onMouseEnter={this.toggleArrow} onMouseLeave={this.toggleArrow}>
              <Cross fill={foregroundColor} />
            </div>

            {/* Cursor arrow/spinner */}
            {this.state.showArrow ? <Cursor fill={foregroundColor} /> : null}

          </div>
        ) : (
          <div className="fs" style={{
            opacity: 0,
            transition: `opacity ${300}ms`
          }} />
        )}
      </div>
    )
  }
}

export default observer(DesktopGallery)



// WEBPACK FOOTER //
// ./app/javascript/packs/components/DesktopGallery.js